import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import mixpanel from "mixpanel-browser";
import { getStaticPage } from "../../services/data/staticPages";
import { selectUser } from "../../store/slice/user";
import SubPageNav from "../../components/SubPageNav";
import SiteFooter from "../../components/SiteFooter";
import { Style } from "./style";

// Mirrors new-design/o7events-frontend/terms.html: SubPageNav hero, then the
// legal copy. The copy is backend-driven via the admin "Static Pages" > Terms
// & Conditions page (StaticPageController, "legal_body" block) so the O7
// team can keep editing it from the admin dashboard without a redeploy.
const Terms = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, email, name } = useSelector(selectUser);
  const isArabic = language === "ar";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchTerms = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await getStaticPage("terms");
        if (cancelled) return;
        setData(res.data.data);
        mixpanel?.track("Terms & Conditions", {
          email,
          name: name || "anonymous",
        });
      } catch (err) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTerms();
    return () => {
      cancelled = true;
    };
  }, [email, name]);

  const legalBody = data?.legal_body?.[0];
  const body = legalBody?.translation?.[language]?.body || legalBody?.translation?.en?.body;

  return (
    <Box sx={Style.page}>
      <SubPageNav />
      <Box sx={Style.hero}>
        <Typography component="h1" sx={Style.heroTitle(isArabic)}>
          {t("marketing.terms.title")}
        </Typography>
        <Typography sx={Style.subtitle}>{t("marketing.terms.subtitle")}</Typography>
      </Box>

      {loading && (
        <Box sx={Style.loaderWrap}>
          <CircularProgress size={64} color="secondary" />
        </Box>
      )}

      {!loading && error && (
        <Box sx={Style.errorWrap}>
          <Typography sx={Style.errorText}>{t("marketing.terms.loadError")}</Typography>
          <Box component="button" sx={Style.backButton} onClick={() => navigate("/")}>
            {t("marketing.nav.backToHome")}
          </Box>
        </Box>
      )}

      {!loading && !error && body && (
        <Box sx={Style.prose(isArabic)} dangerouslySetInnerHTML={{ __html: body }} />
      )}

      <SiteFooter />
    </Box>
  );
};

export default Terms;
